'use strict';

/**
 * Pict-Provider-PromptAPI
 * -----------------------
 * Browser-side client for the prompt library: generic Prompt CRUD (the
 * caller's own library, owner-scoped server-side) plus the custom routes that
 * cross users or carry provenance - the shared library, freeform paste-ins,
 * runs, posting, forking, version history, and attachments.
 *
 * PromptShapeAPI (below, same file) covers the shape cascade: the platform
 * catalog, the merged Available list the editor consumes, adoption and forks.
 */

const libProviderEntityAPI = require('./Pict-Provider-EntityAPI.js');
const libFrameworkClient = require('./FrameworkClient.js');

const _PromptAPIConfiguration =
{
	ProviderIdentifier: 'PromptAPI',
	EntityName: 'Prompt',
	PluralRoute: 'Prompts',
	IDField: 'IDPrompt'
};

class PictProviderPromptAPI extends libProviderEntityAPI
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _PromptAPIConfiguration, pOptions || {}), pServiceHash);
	}

	_json(pMethod, pPath, pBody)
	{
		let tmpURL = this._url(pPath);
		if (pMethod === 'GET') { return libFrameworkClient.getJSON(this.fable, tmpURL); }
		if (pMethod === 'PUT') { return libFrameworkClient.putJSON(this.fable, tmpURL, pBody || {}); }
		if (pMethod === 'DELETE') { return libFrameworkClient.delJSON(this.fable, tmpURL); }
		return libFrameworkClient.postJSON(this.fable, tmpURL, pBody || {});
	}

	// ---- libraries ----------------------------------------------------------
	mine() { return this._json('GET', 'Prompts/Mine').then((pBody) => pBody.Prompts || []); }
	shared() { return this._json('GET', 'Prompts/Shared').then((pBody) => pBody.Prompts || []); }
	sharedOne(pIDPrompt) { return this._json('GET', 'Prompt/Shared/' + pIDPrompt).then((pBody) => pBody.Prompt || null); }
	sharedWordLists() { return this._json('GET', 'WordLists/Shared').then((pBody) => pBody.WordLists || []); }

	// ---- the refinement cycle ------------------------------------------------
	paste(pDraft) { return this._json('POST', 'Prompt/Paste', pDraft); }
	recordRun(pIDPrompt, pRun) { return this._json('POST', 'Prompt/' + pIDPrompt + '/Run', pRun || {}); }
	post(pIDPrompt) { return this._json('POST', 'Prompt/' + pIDPrompt + '/Post', {}); }
	fork(pIDPrompt, pOverrides) { return this._json('POST', 'Prompt/' + pIDPrompt + '/Fork', pOverrides || {}); }

	// ---- history ---------------------------------------------------------------
	versions(pIDPrompt) { return this._json('GET', 'Prompt/' + pIDPrompt + '/Versions'); }
	runs(pIDPrompt) { return this._json('GET', 'Prompt/' + pIDPrompt + '/Runs'); }

	// ---- generate for a work item -------------------------------------------
	// Craft a Prompt from a shape, pre-filled from the work item, versioned, and attached
	// (server-side). Returns { Prompt, Version, Link, PromptNumber, IDWorkItem }.
	generateForWorkItem(pIDWorkItem, pTypeKey)
	{
		return this._json('POST', 'WorkItem/' + pIDWorkItem + '/GeneratePrompt', { TypeKey: pTypeKey || 'software-development' });
	}

	// Craft ONE combined Prompt covering several work items (an ordered breakdown in the Task
	// segment), attached to every one. Returns { Prompt, Version, Links, PromptNumber, IDWorkItems }.
	generateForWorkItems(pIDWorkItems, pTypeKey)
	{
		return this._json('POST', 'Prompt/GenerateForWorkItems', { IDWorkItems: pIDWorkItems || [], TypeKey: pTypeKey || 'software-development' });
	}

	// ---- attachments ----------------------------------------------------------
	attach(pIDPrompt, pOwnerType, pIDOwner, pNote)
	{
		return this._json('POST', 'Prompt/' + pIDPrompt + '/Attach',
			{ OwnerType: pOwnerType, IDOwner: pIDOwner, Note: pNote || '' });
	}
	linksFor(pOwnerType, pIDOwner)
	{
		return this._json('GET', 'PromptLinks/For/' + pOwnerType + '/' + pIDOwner)
			.then((pBody) => pBody.Links || []);
	}
	detach(pIDPromptLink)
	{
		return libFrameworkClient.delJSON(this.fable, this._url('PromptLink/' + pIDPromptLink))
			.then(() => true);
	}
	// Revise an attachment's prompt text in place: captures a new pinned version from the given Source
	// and re-points the attachment at it, so the MCP run uses the revised text. Owner-only, content.edit.
	revise(pIDPromptLink, pSource)
	{
		return this._json('POST', 'PromptLink/' + pIDPromptLink + '/Revise', { Source: pSource || '' });
	}
}

const _PromptShapeAPIConfiguration =
{
	ProviderIdentifier: 'PromptShapeAPI',
	EntityName: 'PromptShape',
	PluralRoute: 'PromptShapes',
	IDField: 'IDPromptShape'
};

class PictProviderPromptShapeAPI extends libProviderEntityAPI
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _PromptShapeAPIConfiguration, pOptions || {}), pServiceHash);
	}

	_json(pMethod, pPath, pBody)
	{
		return PictProviderPromptAPI.prototype._json.call(this, pMethod, pPath, pBody);
	}

	platform() { return this._json('GET', 'PromptShapes/Platform').then((pBody) => pBody.Shapes || []); }
	available() { return this._json('GET', 'PromptShapes/Available').then((pBody) => pBody.Shapes || []); }
	adoptPlatform(pSelector) { return this._json('POST', 'PromptShape/AdoptPlatform', pSelector).then((pBody) => pBody.Shape || null); }
	fork(pIDPromptShape, pOverrides) { return this._json('POST', 'PromptShape/' + pIDPromptShape + '/Fork', pOverrides || {}).then((pBody) => pBody.Shape || null); }
	versions(pIDPromptShape) { return this._json('GET', 'PromptShape/' + pIDPromptShape + '/Versions'); }
}

module.exports = PictProviderPromptAPI;
module.exports.default_configuration = _PromptAPIConfiguration;
module.exports.PromptShapeAPI = PictProviderPromptShapeAPI;
module.exports.PromptShapeAPI.default_configuration = _PromptShapeAPIConfiguration;
